import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Field, Section, Select, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CrawlIssues, CrawlRun, SeoHealth, SeoHistoryEntry } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * SEO Komuta Merkezi (F5-05): yayındaki içeriğin SEO sağlık özeti, dizine açıklık ve SEO değişiklik geçmişi.
 * Her sorun satırında düzenleme ekranına bağlantı.
 */

type Item = SeoHealth['items'][number];
type IssueCode = keyof SeoHealth['summary'];

const editLink = (i: Pick<Item, 'kind' | 'id'>): { to: string; params?: Record<string, string> } =>
  i.kind === 'product'
    ? { to: '/urunler/$id', params: { id: i.id } }
    : i.kind === 'page'
      ? { to: '/sayfalar/$id', params: { id: i.id } }
      : i.kind === 'post'
        ? { to: '/blog/$id', params: { id: i.id } }
        : { to: '/kategoriler' };

export function SeoPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  const [filter, setFilter] = useState<'all' | IssueCode>('all');
  const health = useQuery({
    queryKey: ['seo-health', storeId],
    queryFn: () => api.get<SeoHealth>(`/v1/admin/seo/health?storeId=${storeId}`),
    enabled: !!storeId,
  });
  const d = health.data;
  const codes = d ? (Object.keys(d.summary) as IssueCode[]) : [];
  const items = (d?.items ?? []).filter((i) => filter === 'all' || i.issues.some((x) => x.type === filter));
  return (
    <PageFrame helpKey="page.seo">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <Section
            title={t('admin.seo.indexingTitle')}
            description={t('admin.seo.indexingLead')}
            action={<HelpTip k="seo.indexing" />}
          >
            <p className="flex flex-wrap items-center gap-2">
              <Badge tone={d.indexing.open ? 'moss' : 'amber'}>
                {d.indexing.open ? t('admin.seo.indexOpen') : t('admin.seo.indexClosed')}
              </Badge>
              {!d.indexing.open && (
                <span className="text-sm text-ink-2">
                  {d.indexing.storeStatus !== 'live'
                    ? t('admin.seo.closedStore')
                    : t('admin.seo.closedServer')}
                </span>
              )}
            </p>
            <p className="mt-2 text-sm text-ink-3">
              {t('admin.seo.checked', {
                products: d.checked.products,
                categories: d.checked.categories,
                pages: d.checked.pages,
                posts: d.checked.posts,
              })}
            </p>
          </Section>

          <Section
            title={t('admin.seo.issuesTitle')}
            description={t('admin.seo.issuesLead')}
            action={<HelpTip k="seo.issues" />}
          >
            <Field label={t('admin.seo.filter')} help={<HelpTip k="seo.issue_filter" />} className="max-w-sm">
              {(p) => (
                <Select {...p} value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
                  <option value="all">{t('admin.seo.filterAll', { count: d.total })}</option>
                  {codes.map((c) => (
                    <option key={c} value={c} disabled={!d.summary[c]}>
                      {t(`admin.seo.issueNames.${c}`)} ({d.summary[c]})
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {items.length === 0 ? (
              <EmptyState title={t('admin.seo.noIssues')} description={t('admin.seo.noIssuesLead')} />
            ) : (
              <div className="mt-3 overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" aria-label={t('admin.seo.issuesTitle')}>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.colKind')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.colTitle')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.colIssues')}</th>
                      <th className="px-3 py-2 text-end font-medium">
                        <span className="sr-only">{t('common.edit')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => {
                      const link = editLink(i);
                      return (
                        <tr key={`${i.kind}-${i.id}`} className="border-t border-line align-top">
                          <td className="px-3 py-2 whitespace-nowrap">{t(`admin.seo.kinds.${i.kind}`)}</td>
                          <td className="px-3 py-2">
                            {i.title}
                            <span className="block text-xs text-ink-3">{i.path}</span>
                          </td>
                          <td className="px-3 py-2">
                            <ul className="flex flex-wrap gap-1">
                              {i.issues.map((x, n) => (
                                <li key={n}>
                                  <Badge
                                    tone={
                                      x.type === 'gtin_invalid' || x.type === 'title_duplicate'
                                        ? 'rose'
                                        : 'amber'
                                    }
                                  >
                                    {t(`admin.seo.issueNames.${x.type}`)}
                                    {x.detail ? ` (${x.detail})` : ''}
                                  </Badge>
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="px-3 py-2 text-end">
                            <Link
                              to={link.to}
                              params={link.params as never}
                              className="text-pine hover:underline"
                              aria-label={t('admin.seo.editItem', { title: i.title })}
                            >
                              {t('common.edit')}
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {d.total > d.items.length && (
                  <p className="px-3 py-2 text-sm text-ink-3">
                    {t('admin.seo.more', { shown: d.items.length, total: d.total })}
                  </p>
                )}
              </div>
            )}
          </Section>
          <CrawlSection storeId={storeId} />
          <HistorySection />
        </>
      )}
    </PageFrame>
  );
}

function HistorySection() {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['seo-history'],
    queryFn: () => api.get<SeoHistoryEntry[]>('/v1/admin/seo/history?limit=50'),
  });
  return (
    <Section
      title={t('admin.seo.historyTitle')}
      description={t('admin.seo.historyLead')}
      action={<HelpTip k="seo.history" />}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.seo.historyEmpty')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm" role="list">
          {q.data.map((h) => (
            <li key={h.id} className="space-y-1 px-4 py-3">
              <p className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-medium">{h.title ?? '—'}</span>
                <span className="text-ink-3">{t(`admin.seo.kinds.${h.entityType}`)}</span>
                <span className="text-ink-3">
                  {formatDate(h.at, true)}
                  {h.actor ? ` · ${h.actor}` : ''}
                </span>
              </p>
              <ul className="space-y-0.5 text-ink-2">
                {h.changes.map((c) => (
                  <li key={c.field}>
                    {t(`admin.seo.fields.${c.field}`)}: <s className="text-ink-3">{c.before ?? '—'}</s> →{' '}
                    <span>{c.after ?? '—'}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const RUN_TONE = { queued: 'slate', running: 'amber', completed: 'moss', failed: 'rose' } as const;
const SEVERITY_TONE = { error: 'rose', warning: 'amber', info: 'slate' } as const;
const FAIL_REASONS = [
  'no_domain',
  'site_protected',
  'home_unreachable',
  'https_required',
  'private_address',
] as const;
const failReason = (t: ReturnType<typeof useT>, code: string | null) =>
  t(
    `admin.seo.crawl.failReasons.${(FAIL_REASONS as readonly string[]).includes(code ?? '') ? code : 'unknown'}`,
  );

/** Sorun ayrıntısını okunur metne çevirir (bağlantı veren sayfalar, durum kodu, zincir, canonical…). */
function detailText(t: ReturnType<typeof useT>, d: Record<string, unknown>): string {
  const parts: string[] = [];
  if (typeof d.status === 'number') parts.push(t('admin.seo.crawl.detailStatus', { status: d.status }));
  if (Array.isArray(d.from) && d.from.length)
    parts.push(t('admin.seo.crawl.detailFrom', { list: d.from.join(', ') }));
  if (Array.isArray(d.chain)) parts.push(d.chain.join(' → '));
  if (typeof d.to === 'string') parts.push(`→ ${d.to}`);
  if (typeof d.canonical === 'string') parts.push(t('admin.seo.crawl.detailCanonical', { url: d.canonical }));
  if (typeof d.count === 'number') parts.push(t('admin.seo.crawl.detailCount', { count: d.count }));
  if (Array.isArray(d.paths) && d.paths.length)
    parts.push(t('admin.seo.crawl.detailAlso', { list: d.paths.join(', ') }));
  return parts.join(' · ');
}

/** Teknik site tarayıcısı (F5-06): elle başlatma, son taramalar ve son tamamlanan taramanın sorunları. */
function CrawlSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [type, setType] = useState('all');
  const runs = useQuery({
    queryKey: ['seo-crawls', storeId],
    queryFn: () => api.get<CrawlRun[]>(`/v1/admin/seo/crawls?storeId=${storeId}`),
    refetchInterval: (q) =>
      q.state.data?.some((r) => r.status === 'queued' || r.status === 'running') ? 3000 : false,
  });
  const latest = runs.data?.[0];
  const done = runs.data?.find((r) => r.status === 'completed');
  const issues = useQuery({
    queryKey: ['seo-crawl-issues', done?.id],
    queryFn: () => api.get<CrawlIssues>(`/v1/admin/seo/crawls/${done!.id}/issues`),
    enabled: !!done,
  });
  const start = useApiMutation(() => api.post('/v1/admin/seo/crawls', { storeId }), {
    invalidate: [['seo-crawls', storeId]],
    success: t('admin.seo.crawl.started'),
  });
  const busy = latest?.status === 'queued' || latest?.status === 'running';
  const types = [...new Set((issues.data?.summary ?? []).map((s) => s.type))];
  const list = (issues.data?.items ?? []).filter((i) => type === 'all' || i.type === type);
  return (
    <Section
      title={t('admin.seo.crawl.title')}
      description={t('admin.seo.crawl.lead')}
      action={<HelpTip k="seo.crawl" />}
    >
      <div className="flex flex-wrap items-center gap-3">
        {can('seo.write') && (
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              loading={start.isPending}
              disabled={busy || !storeId}
              onClick={() => start.mutate(undefined)}
            >
              <Play className="h-4 w-4" aria-hidden />
              {t('admin.seo.crawl.start')}
            </Button>
            <HelpTip k="seo.crawl_start" />
          </span>
        )}
        {latest && (
          <p className="flex flex-wrap items-center gap-2 text-sm" aria-live="polite">
            <Badge tone={RUN_TONE[latest.status]}>{t(`admin.seo.crawl.status.${latest.status}`)}</Badge>
            <span className="text-ink-2">
              {formatDate(latest.createdAt, true)} · {t(`admin.seo.crawl.trigger.${latest.trigger}`)}
              {latest.status === 'completed' &&
                ` · ${t('admin.seo.crawl.result', { pages: latest.pagesCrawled, issues: latest.issuesFound })}`}
              {latest.error === 'truncated' && ` · ${t('admin.seo.crawl.truncated')}`}
              {latest.status === 'failed' &&
                ` · ${t('admin.seo.crawl.failed', { reason: failReason(t, latest.error) })}`}
            </span>
          </p>
        )}
      </div>
      {!runs.data?.length && <p className="mt-3 text-sm text-ink-2">{t('admin.seo.crawl.none')}</p>}
      {done && issues.data && (
        <div className="mt-4 space-y-3">
          {done.id !== latest?.id && (
            <p className="text-sm text-ink-3">
              {t('admin.seo.crawl.showingPrevious', { time: formatDate(done.createdAt, true) })}
            </p>
          )}
          {issues.data.items.length === 0 ? (
            <p className="text-sm text-ink-2">{t('admin.seo.crawl.clean')}</p>
          ) : (
            <>
              <Field
                label={t('admin.seo.crawl.filter')}
                help={<HelpTip k="seo.crawl_filter" />}
                className="max-w-sm"
              >
                {(p) => (
                  <Select {...p} value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="all">
                      {t('admin.seo.crawl.filterAll', { count: issues.data!.items.length })}
                    </option>
                    {types.map((ty) => (
                      <option key={ty} value={ty}>
                        {t(`admin.seo.crawl.types.${ty}`)} (
                        {issues.data!.summary.filter((s) => s.type === ty).reduce((a, s) => a + s.n, 0)})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" aria-label={t('admin.seo.crawl.tableLabel')}>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.crawl.colSeverity')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.crawl.colType')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.crawl.colPath')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('admin.seo.crawl.colDetail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((i) => (
                      <tr key={i.id} className="border-t border-line align-top">
                        <td className="px-3 py-2">
                          <Badge tone={SEVERITY_TONE[i.severity]}>
                            {t(`admin.seo.crawl.severity.${i.severity}`)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">{t(`admin.seo.crawl.types.${i.type}`)}</td>
                        <td className="px-3 py-2 break-all font-mono text-xs">{i.path}</td>
                        <td className="px-3 py-2 text-ink-2">{detailText(t, i.detail)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </Section>
  );
}
